import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import ImageUpload from './ImageUpload';
import EnhancedRichTextEditor from './EnhancedRichTextEditor';

const VisionMissionEdit = () => {
  const [visionMissionData, setVisionMissionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    bannerTitle: '',
    bannerSubtitle: '',
    bannerImage: '',
    visionTitle: '',
    visionContent: '',
    visionImage: '',
    missionTitle: '',
    missionContent: '',
    missionImage: '',
    coreValuesTitle: '',
    coreValues: []
  });

  useEffect(() => {
    fetchVisionMissionData();
  }, []);

  const fetchVisionMissionData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getVisionMission();
      const data = response.data;
      
      setVisionMissionData(data);
      setFormData({
        bannerTitle: data.bannerTitle || '',
        bannerSubtitle: data.bannerSubtitle || '',
        bannerImage: data.bannerImage || '',
        visionTitle: data.visionTitle || '',
        visionContent: data.visionContent || '',
        visionImage: data.visionImage || '',
        missionTitle: data.missionTitle || '',
        missionContent: data.missionContent || '',
        missionImage: data.missionImage || '',
        coreValuesTitle: data.coreValuesTitle || '',
        coreValues: data.coreValues || []
      });
    } catch (error) {
      setError('Failed to load vision mission data');
      console.error('Error fetching vision mission:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const handleObjectArrayChange = (field, index, key, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => 
        i === index ? { ...item, [key]: value } : item
      )
    }));
  };


  const addCoreValue = () => {
    setFormData(prev => ({
      ...prev,
      coreValues: [...prev.coreValues, { icon: '', title: '', description: '' }]
    }));
  };

  const removeCoreValue = (index) => {
    setFormData(prev => ({
      ...prev,
      coreValues: prev.coreValues.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (visionMissionData?.id) {
        await apiService.updateVisionMission(visionMissionData.id, formData);
        setSuccess('Vision & Mission updated successfully!');
      } else {
        await apiService.createVisionMission(formData);
        setSuccess('Vision & Mission created successfully!');
      }
      
      // Refresh data
      await fetchVisionMissionData();
    } catch (error) {
      setError('Failed to save vision mission data');
      console.error('Error saving vision mission:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate">
          <i className="fas fa-spinner fa-spin mr-2 text-crimson"></i>
          Loading vision mission data...
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans">
      <div className="mb-8">
        <h1 className="text-3xl font-sans font-bold text-slate mb-2">
          Vision & Mission Management
        </h1>
        <p className="text-gray-600">
          Manage the content for the Vision & Mission page including banner, vision, mission, and core values.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-check-circle mr-2"></i>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Banner Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Banner Section</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Banner Title
              </label>
              <input
                type="text"
                value={formData.bannerTitle}
                onChange={(e) => handleInputChange('bannerTitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="Vision & Mission"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Banner Subtitle
              </label>
              <textarea
                value={formData.bannerSubtitle}
                onChange={(e) => handleInputChange('bannerSubtitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={2}
                placeholder="Subtitle for the banner"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Banner Image
              </label>
              <ImageUpload
                value={formData.bannerImage}
                onChange={(value) => handleInputChange('bannerImage', value)}
                placeholder="Banner background image filename"
              />
            </div>
          </div>
        </div>

        {/* Vision Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Vision Section</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Vision Title
              </label>
              <input
                type="text"
                value={formData.visionTitle}
                onChange={(e) => handleInputChange('visionTitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="Our Vision"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Vision Content
              </label>
              <EnhancedRichTextEditor
                value={formData.visionContent}
                onChange={(value) => handleInputChange('visionContent', value)}
                placeholder="Enter your vision content here..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Vision Image
              </label>
              <ImageUpload
                value={formData.visionImage}
                onChange={(value) => handleInputChange('visionImage', value)}
                placeholder="Vision section image filename"
              />
            </div>
          </div>
        </div>

        {/* Mission Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Mission Section</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Mission Title
              </label>
              <input
                type="text"
                value={formData.missionTitle}
                onChange={(e) => handleInputChange('missionTitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="Our Mission"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Mission Content
              </label>
              <EnhancedRichTextEditor
                value={formData.missionContent}
                onChange={(value) => handleInputChange('missionContent', value)}
                placeholder="Enter your mission content here..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Mission Image
              </label>
              <ImageUpload
                value={formData.missionImage}
                onChange={(value) => handleInputChange('missionImage', value)}
                placeholder="Mission section image filename"
              />
            </div>
          </div>
        </div>

        {/* Core Values Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Core Values Section</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">
                Core Values Title
              </label>
              <input
                type="text"
                value={formData.coreValuesTitle}
                onChange={(e) => handleInputChange('coreValuesTitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                placeholder="Our Core Values"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-slate">
                  Core Values
                </label>
                <button
                  type="button"
                  onClick={addCoreValue}
                  className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 flex items-center gap-2 text-sm"
                >
                  <i className="fas fa-plus"></i>
                  Add Value
                </button>
              </div>
              
              {formData.coreValues.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <i className="fas fa-info-circle mb-2 text-2xl"></i>
                  <p>No core values added yet. Click "Add Value" to get started.</p>
                </div>
              )}
              
              <div className="space-y-4">
                {formData.coreValues.map((value, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                    <button
                      type="button"
                      onClick={() => removeCoreValue(index)}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                      title="Remove this core value"
                    >
                      <i className="fas fa-times-circle"></i>
                    </button>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate mb-1">
                          Icon (FontAwesome class)
                        </label>
                        <input
                          type="text"
                          value={value.icon}
                          onChange={(e) => handleObjectArrayChange('coreValues', index, 'icon', e.target.value)}
                          className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                          placeholder="fas fa-star"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate mb-1">
                          Title
                        </label>
                        <input
                          type="text"
                          value={value.title}
                          onChange={(e) => handleObjectArrayChange('coreValues', index, 'title', e.target.value)}
                          className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                          placeholder="Excellence"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate mb-1">
                          Description
                        </label>
                        <textarea
                          value={value.description}
                          onChange={(e) => handleObjectArrayChange('coreValues', index, 'description', e.target.value)}
                          className="w-full px-3 py-2 border border-border_gray rounded text-sm"
                          rows={2}
                          placeholder="Description of this core value"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-crimson text-white rounded-lg font-medium hover:bg-crimson/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Save Vision & Mission
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VisionMissionEdit;